import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { result } from 'lodash'
import FocusTrap from 'focus-trap-react'
import Button from 'src/Components/Button'
import { If } from 'src/Components/Conditionals'
import Text from 'src/Components/Text'
import BodyScrollLock from 'src/Components/BodyScrollLock'
import {
  enableBodyScroll,
  disableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock'

import styles from './Modal.module.scss'

const defaultProps = {
  backdropOpacity: '10',
  overrideContent: false,
  type: 'info',
  size: 'm',
  resetScroll: false,
  scrollOnWrapper: false
}

const propTypes = {
  overrideContent: PropTypes.bool.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  subHeader: PropTypes.node,
  toggle: PropTypes.func,
  darkHeader: PropTypes.bool,
  backdropOpactiy: PropTypes.oneOf(['0', '5', '10']),
  className: PropTypes.string,
  type: PropTypes.oneOf(['info', 'error', 'quiet', 'warn']),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  size: PropTypes.oneOf(['m', 'l']),
  scrollOnWrapper: PropTypes.bool,
  resetScroll: PropTypes.bool
}

const ModalClose = ({ toggle, className }) => (
  <Button.Close
    className={classnames(styles.close, className)}
    onClick={e => {
      toggle()
      e.preventDefault()
    }}
  />
)

const ModalInner = ({ children }) => (
  <div className={styles.inner}>{children}</div>
)

const ModalBody = ({
  title,
  subtitle,
  subheader,
  darkHeader,
  children,
  toggle
}) => (
  <div>
    <div
      className={classnames(styles.header, {
        [styles.dark]: darkHeader,
        [styles.noSubheader]: !subheader
      })}
    >
      {toggle && <ModalClose toggle={toggle} />}
      <Text element='h2' weight='light' size='xl' className={styles.title}>
        {title}
      </Text>
      {subtitle && (
        <Text color='quiet' size='s' align='center' className={styles.subtitle}>
          {subtitle}
        </Text>
      )}
      {subheader}
    </div>
    <ModalInner>{children}</ModalInner>
  </div>
)

const handleEscape = toggle => e => e.key === 'Escape' && toggle && toggle()

const Modal = props => {
  let wrapper
  useEffect(() => {
    if (wrapper) {
      if (props.scrollOnWrapper) {
        clearAllBodyScrollLocks()
        disableBodyScroll(wrapper)
      }

      if (props.resetScroll) {
        if (typeof wrapper.scroll === 'function') {
          wrapper.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
          })
        } else {
          wrapper.scrollTop = 0
          wrapper.scrollLeft = 0
        }
      }

      if (props.scrollOnWrapper) {
        return () => {
          enableBodyScroll(wrapper)
        }
      }
    }
  }, [props.children, props.resetScroll, props.scrollOnWrapper, wrapper])

  const levelClass = props.level && styles[`level${props.level}`]
  const typeClass = styles[props.type]

  return (
    <FocusTrap>
      <div
        className={classnames(
          styles.wrapper,
          levelClass,
          props.resetScroll && styles.allowScroll
        )}
        onKeyDown={handleEscape(props.toggle)}
        ref={ref => (wrapper = ref)}
      >
        <div
          className={classnames(
            styles.backdrop,
            styles[`opacity-${props.backdropOpacity}`],
            levelClass
          )}
          onClick={() => result(props, 'toggle')}
        />
        <BodyScrollLock
          className={classnames(
            styles.container,
            styles[`size-${props.size}`],
            props.progress && styles.withProgress,
            levelClass,
            typeClass,
            props.className
          )}
        >
          {props.overrideContent ? props.children : <ModalBody {...props} />}
          <If condition={props.progress}>
            <div
              className={styles.progress}
              style={{ width: `${props.progress || 0}%` }}
            />
          </If>
        </BodyScrollLock>
      </div>
    </FocusTrap>
  )
}

Modal.defaultProps = defaultProps
Modal.propTypes = propTypes

Modal.Close = ModalClose
Modal.Inner = ModalInner

export default Modal
